import { useEffect, useState } from "react";
import { FilePdf } from "@phosphor-icons/react";
import api from "../../lib/api";
import { Field, Section, Radio } from "./fields";
import { SignaturePad } from "../SignaturePad";

export const emptyForm5 = {
  version: "v1", effective_date: "", reviewed_with_client: "", rep_participated: "",
  changes_requested: "", changes_completed: "",
  signer_type: "", client_signature_date: "", client_signature_png: null,
  agency_rep: "", agency_signature_date: "", agency_signature_png: null,
  status: "Draft",
};

const ACK_TEXT = "I participated in the development and review of my care plan. The goals, schedule, services, task codes, assistance levels, preferences, safety information and backup arrangements were reviewed with me, and I had the opportunity to ask questions and request changes.";
const YN = ["Yes", "No"];

export default function Form5Approval({ data, onChange, clientId, clientCode }) {
  const [clientName, setClientName] = useState("");
  const set = (k) => (e) => onChange({ ...data, [k]: e.target.value });
  const setR = (k) => (v) => onChange({ ...data, [k]: v });

  useEffect(() => {
    if (!clientId) return;
    api.get(`/clients/${clientId}`).then((r) => {
      setClientName(r.data.forms?.intake?.full_name || "");
      const eff = r.data.forms?.careplan?.effective_date;
      if (eff && !data.effective_date) onChange({ ...data, effective_date: eff });
    }).catch(() => {});
    // eslint-disable-next-line
  }, [clientId]);

  return (
    <div className="space-y-6" data-testid="form5-form">
      {clientId && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
          <span className="text-sm text-stone-500">Client:</span>
          <code className="rounded-lg border border-stone-200 bg-accent px-3 py-1 text-sm font-semibold text-accent-foreground" data-testid="form5-client-code">{clientCode}</code>
          <span className="text-sm text-stone-500">Client:</span>
          <span className="text-sm font-semibold text-stone-900" data-testid="form5-client-name">{clientName || "—"}</span>
          <span className="text-sm text-stone-500">Version:</span>
          <span className="text-sm font-semibold text-stone-900" data-testid="form5-version">{data.version || "v1"}</span>
          <a href={`${api.defaults.baseURL}/clients/${clientId}/forms/approval/pdf`} download
            className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
            data-testid="form5-pdf-button">
            <FilePdf size={14} /> Download PDF
          </a>
        </div>
      )}

      <Section title="Review">
        <Field label="Effective date" tid="f5-effective-date" type="date" value={data.effective_date} onChange={set("effective_date")} />
        <Radio label="Plan reviewed with client?" options={YN} value={data.reviewed_with_client} onChange={setR("reviewed_with_client")} tid="f5-reviewed" />
        <Radio label="Legal representative participated (if applicable)?" options={YN} value={data.rep_participated} onChange={setR("rep_participated")} tid="f5-rep-participated" />
        <Radio label="Client/representative requested changes?" options={YN} value={data.changes_requested} onChange={setR("changes_requested")} tid="f5-changes-requested" />
        {data.changes_requested === "Yes" && (
          <Radio label="Changes completed?" options={YN} value={data.changes_completed} onChange={setR("changes_completed")} tid="f5-changes-completed" />
        )}
      </Section>

      <div className="rounded-lg border border-stone-200 bg-accent p-5 text-sm leading-relaxed text-stone-700" data-testid="f5-acknowledgment">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone-500">Acknowledgment</p>
        {ACK_TEXT}
      </div>

      <Section title="Client / Representative Signature">
        <Radio label="Signer type" options={["Client", "POA", "Guardian", "Other authorized representative"]} value={data.signer_type} onChange={setR("signer_type")} tid="f5-signer-type" />
        <Field label="Date" tid="f5-client-signature-date" type="date" value={data.client_signature_date} onChange={set("client_signature_date")} />
        <div className="md:col-span-2">
          <SignaturePad onChange={(png) => onChange({ ...data, client_signature_png: png })} />
          {data.client_signature_png && <p className="mt-1 text-xs font-medium text-[#1e8449]" data-testid="f5-client-signed">Signature captured</p>}
        </div>
      </Section>

      <Section title="Agency Representative">
        <Field label="Agency representative name" tid="f5-agency-rep" value={data.agency_rep} onChange={set("agency_rep")} />
        <Field label="Date" tid="f5-agency-signature-date" type="date" value={data.agency_signature_date} onChange={set("agency_signature_date")} />
        <div className="md:col-span-2">
          <SignaturePad onChange={(png) => onChange({ ...data, agency_signature_png: png })} />
          {data.agency_signature_png && <p className="mt-1 text-xs font-medium text-[#1e8449]" data-testid="f5-agency-signed">Signature captured</p>}
        </div>
      </Section>

      <Section title="Status">
        <Radio label="Care plan status" options={["Draft", "Awaiting Approval", "Approved", "Superseded"]} value={data.status} onChange={setR("status")} tid="f5-status" />
      </Section>
    </div>
  );
}
